import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'

export interface KPIData {
  inspections: {
    total: number
    completed: number
    pending: number
    overdue: number
    completionRate: number
    averageHealthScore: number
  }
  maintenance: {
    totalTickets: number
    openTickets: number
    inProgressTickets: number
    completedTickets: number
    averageResolutionTime: number
    criticalTickets: number
  }
  equipment: {
    totalEquipment: number
    activeEquipment: number
    equipmentInMaintenance: number
    equipmentHealthScore: number
    equipmentUtilization: number
  }
  inventory: {
    totalItems: number
    lowStockItems: number
    outOfStockItems: number
    inventoryValue: number
    stockTurnoverRate: number
  }
  alerts: {
    criticalAlerts: number
    highPriorityAlerts: number
    totalActiveAlerts: number
    resolvedAlertsToday: number
  }
}

export interface RealtimeData {
  activeInspections: number
  onlineInspectors: number
  pendingApprovals: number
  criticalAlerts: number
  systemStatus: 'healthy' | 'warning' | 'critical'
  lastUpdated: string
}

export interface EquipmentPerformanceAnalytics {
  equipmentList: Array<{
    equipmentId: string
    equipmentName: string
    healthScore: number
    inspectionCount: number
    lastInspectionDate: string
    alertCount: number
    maintenanceTickets: number
    status: string
    trend: 'improving' | 'stable' | 'declining'
  }>
  performanceDistribution: {
    excellent: number
    good: number
    fair: number
    poor: number
  }
  topPerformers: Array<{
    equipmentId: string
    equipmentName: string
    healthScore: number
  }>
  needsAttention: Array<{
    equipmentId: string
    equipmentName: string
    healthScore: number
    issues: string[]
  }>
}

export interface AlertSummary {
  alerts: Array<{
    id: string
    type: 'inspection' | 'maintenance' | 'inventory' | 'system'
    severity: 'low' | 'medium' | 'high' | 'critical'
    title: string
    description: string
    equipmentId?: string
    equipmentName?: string
    createdAt: string
    isResolved: boolean
    resolvedAt?: string
  }>
  summary: {
    total: number
    bySeverity: Record<string, number>
    byType: Record<string, number>
    unresolved: number
    resolvedToday: number
  }
}

export interface DashboardFilters {
  dateFrom?: string
  dateTo?: string
  departmentId?: string
  equipmentIds?: string[]
}

export interface RealtimeUpdate {
  type: string
  data: any
  timestamp: string
}

interface DashboardState {
  kpiData: KPIData | null
  realtimeData: RealtimeData | null
  equipmentPerformance: EquipmentPerformanceAnalytics | null
  alertSummary: AlertSummary | null
  realtimeUpdates: RealtimeUpdate[]
  filters: DashboardFilters
  loading: {
    kpi: boolean
    realtime: boolean
    equipment: boolean
    alerts: boolean
  }
  error: string | null
  lastUpdated: string | null
  isConnected: boolean
}

const initialState: DashboardState = {
  kpiData: null,
  realtimeData: null,
  equipmentPerformance: null,
  alertSummary: null,
  realtimeUpdates: [],
  filters: {},
  loading: {
    kpi: false,
    realtime: false,
    equipment: false,
    alerts: false
  },
  error: null,
  lastUpdated: null,
  isConnected: false
}

// Async thunks
export const fetchKPIData = createAsyncThunk(
  'dashboard/fetchKPIData',
  async (filters: DashboardFilters = {}, { rejectWithValue }) => {
    try {
      const data = await apiService.fetchKPIData(filters)
      return data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch KPI data')
    }
  }
)

export const fetchRealtimeData = createAsyncThunk(
  'dashboard/fetchRealtimeData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.fetchRealtimeData()
      return data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch realtime data')
    }
  }
)

export const fetchEquipmentPerformance = createAsyncThunk(
  'dashboard/fetchEquipmentPerformance',
  async (filters: DashboardFilters = {}, { rejectWithValue }) => {
    try {
      const data = await apiService.fetchEquipmentPerformance(filters)
      return data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch equipment performance')
    }
  }
)

export const fetchAlertSummary = createAsyncThunk(
  'dashboard/fetchAlertSummary',
  async (limit: number = 50, { rejectWithValue }) => {
    try {
      const data = await apiService.fetchAlertSummary(limit)
      return data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch alert summary')
    }
  }
)

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<DashboardFilters>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    updateRealtimeData: (state, action: PayloadAction<RealtimeData>) => {
      state.realtimeData = action.payload
      state.lastUpdated = new Date().toISOString()
    },
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },
    updateKPIs: (state, action: PayloadAction<Partial<KPIData>>) => {
      if (state.kpiData) {
        state.kpiData = { ...state.kpiData, ...action.payload }
      }
      state.lastUpdated = new Date().toISOString()
    },
    addRealtimeUpdate: (state, action: PayloadAction<RealtimeUpdate>) => {
      state.realtimeUpdates.unshift(action.payload)
      // Keep only the latest 50 updates
      if (state.realtimeUpdates.length > 50) {
        state.realtimeUpdates = state.realtimeUpdates.slice(0, 50)
      }
      state.lastUpdated = new Date().toISOString()
    },
    clearRealtimeUpdates: (state) => {
      state.realtimeUpdates = []
    }
  },
  extraReducers: (builder) => {
    // KPI Data
    builder
      .addCase(fetchKPIData.pending, (state) => {
        state.loading.kpi = true
        state.error = null
      })
      .addCase(fetchKPIData.fulfilled, (state, action) => {
        state.loading.kpi = false
        state.kpiData = action.payload as KPIData
        state.lastUpdated = new Date().toISOString()
      })
      .addCase(fetchKPIData.rejected, (state, action) => {
        state.loading.kpi = false
        state.error = action.payload as string || 'Failed to fetch KPI data'
      })

    // Realtime Data
    builder
      .addCase(fetchRealtimeData.pending, (state) => {
        state.loading.realtime = true
      })
      .addCase(fetchRealtimeData.fulfilled, (state, action) => {
        state.loading.realtime = false
        state.realtimeData = action.payload as RealtimeData
        state.lastUpdated = new Date().toISOString()
      })
      .addCase(fetchRealtimeData.rejected, (state, action) => {
        state.loading.realtime = false
        state.error = action.payload as string || 'Failed to fetch realtime data'
      })

    // Equipment Performance
    builder
      .addCase(fetchEquipmentPerformance.pending, (state) => {
        state.loading.equipment = true
      })
      .addCase(fetchEquipmentPerformance.fulfilled, (state, action) => {
        state.loading.equipment = false
        state.equipmentPerformance = action.payload as EquipmentPerformanceAnalytics
      })
      .addCase(fetchEquipmentPerformance.rejected, (state, action) => {
        state.loading.equipment = false
        state.error = action.payload as string || 'Failed to fetch equipment performance'
      })

    // Alert Summary
    builder
      .addCase(fetchAlertSummary.pending, (state) => {
        state.loading.alerts = true
      })
      .addCase(fetchAlertSummary.fulfilled, (state, action) => {
        state.loading.alerts = false
        state.alertSummary = action.payload as AlertSummary
      })
      .addCase(fetchAlertSummary.rejected, (state, action) => {
        state.loading.alerts = false
        state.error = action.payload as string || 'Failed to fetch alert summary'
      })
  }
})

export const { 
  setFilters, 
  clearError, 
  updateRealtimeData, 
  setConnectionStatus, 
  updateKPIs, 
  addRealtimeUpdate, 
  clearRealtimeUpdates 
} = dashboardSlice.actions
export default dashboardSlice.reducer